/**
 * NumerosGrid — tabla de ventas por número (00-99) con:
 *  - Relleno completo 00-99 aunque no haya ventas
 *  - Bloques visuales de 10 en orden numérico (separador cada 10 filas por columna)
 *  - Orden por número (default) o por mayor monto descendente
 *  - Codificación por color: sin ventas=gris, 1-999=azul, 1000-4999=naranja, 5000+=rojo
 *  - Fila de totales al pie
 *  - Botón de impresión (abre ventana limpia)
 *  Soporta tipos: NORMAL, REVENTADO y MEGA.
 *
 *  IMPORTANTE: recibe `rows` con valores NUMÉRICOS (no strings formateados).
 *  Las páginas deben pasar los datos crudos de la API.
 */

import React, { useMemo, useRef, useState } from 'react';
import { isNativePrintSelected, printRawByDestination } from '../utils/printer';

export type TipoNumerosGrid = 'NORMAL' | 'REVENTADO' | 'MEGA';

export interface NumerosGridProps {
  rows: any[];
  tipo: TipoNumerosGrid;
  hidePrintButton?: boolean;
  hideZerosInMontoOrder?: boolean;
}

// ---------- utilidades ----------

// Heatmap por participación porcentual (vendido / total_sorteo)
function getParticipacionColor(p: number): React.CSSProperties {
  if (p > 0.25) return { backgroundColor: '#ef4444', color: '#fff' };     // >25% rojo
  if (p > 0.15) return { backgroundColor: '#fb923c', color: '#7c2d12' };  // >15% naranja
  if (p > 0.07) return { backgroundColor: '#fde047', color: '#713f12' };  // >7%  amarillo
  if (p > 0.03) return { backgroundColor: '#86efac', color: '#14532d' };  // >3%  verde
  return {};                                                                // ≤3%  sin color
}

function getTotalForRow(row: any, tipo: TipoNumerosGrid): number {
  if (tipo === 'NORMAL') return Number(row.vendido) || 0;
  if (tipo === 'REVENTADO')
    return (Number(row.vendido_rev1) || 0) + (Number(row.vendido_rev2) || 0);
  // MEGA
  return (
    (Number(row.vendido_rev1) || 0) +
    (Number(row.vendido_rev2) || 0)
  );
}

function fmt(n: number): string {
  if (n === 0) return '–';
  return Math.trunc(n).toLocaleString('es-CR');
}

/**
 * Construye el arreglo completo 00-99 sumando montos por número.
 * Así, MEGA con varias filas por `numero` (distintos num2) se agrega correctamente.
 */
function buildComplete(rows: any[], tipo: TipoNumerosGrid): any[] {
  const map = new Map<string, any>();
  for (let i = 0; i < 100; i++) {
    const num = String(i).padStart(2, '0');
    const base: any = { numero: num };
    if (tipo === 'NORMAL') {
      base.vendido = 0;
    } else if (tipo === 'REVENTADO') {
      base.vendido_rev1 = 0;
      base.vendido_rev2 = 0;
    } else {
      base.vendido_rev1 = 0;
      base.vendido_rev2 = 0;
    }
    map.set(num, base);
  }

  for (const r of rows) {
    const key = String(r.numero).padStart(2, '0');
    if (!map.has(key)) continue;
    const ex = map.get(key)!;
    const flags = {
      _restringido: r._restringido || ex._restringido,
      _top: r._top || ex._top,
      _highlight: r._highlight || ex._highlight,
    };
    if (tipo === 'NORMAL') {
      map.set(key, { ...ex, ...flags, vendido: ex.vendido + (Number(r.vendido) || 0) });
    } else if (tipo === 'REVENTADO') {
      map.set(key, {
        ...ex,
        ...flags,
        vendido_rev1: ex.vendido_rev1 + (Number(r.vendido_rev1) || 0),
        vendido_rev2: ex.vendido_rev2 + (Number(r.vendido_rev2) || 0),
      });
    } else {
      // MEGA: acumular rev1/rev2 (sin segundo número)
      map.set(key, {
        ...ex,
        ...flags,
        vendido_rev1: ex.vendido_rev1 + (Number(r.vendido_rev1) || 0),
        vendido_rev2: ex.vendido_rev2 + (Number(r.vendido_rev2) || 0),
      });
    }
  }

  return Array.from(map.values());
}

function chunkCols(items: any[], cols: number): any[][] {
  const perCol = Math.ceil(items.length / cols);
  return Array.from({ length: cols }, (_, i) =>
    items.slice(i * perCol, (i + 1) * perCol),
  );
}

// ---------- estilos compartidos ----------

const btnBase: React.CSSProperties = {
  padding: '11px 16px',
  fontFamily: 'monospace',
  fontSize: '13px',
  cursor: 'pointer',
  border: '1px solid #d1d5db',
  borderRadius: '3px',
  lineHeight: '1.4',
  minHeight: '44px',
};

const thBase: React.CSSProperties = {
  padding: '5px 6px',
  textAlign: 'left',
  fontWeight: 'bold',
  borderBottom: '1px solid #e5e7eb',
  borderRight: '1px solid #d1d5db',
  fontSize: '12px',
  backgroundColor: '#e5e7eb',
  position: 'sticky' as const,
  top: 0,
};

// ---------- componente principal ----------

export const NumerosGrid = React.memo<NumerosGridProps>(({ rows, tipo, hidePrintButton, hideZerosInMontoOrder }) => {
  const [ordenMonto, setOrdenMonto] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  // MEGA usa 5 grupos (igual que REVENTADO)
  const COLS = 5;

  const numerosOrdenados = useMemo(() => {
    const all = buildComplete(rows, tipo);
    if (ordenMonto) {
      const sortable = hideZerosInMontoOrder
        ? all.filter((row) => getTotalForRow(row, tipo) > 0)
        : all;
      return [...sortable].sort(
        (a, b) => getTotalForRow(b, tipo) - getTotalForRow(a, tipo),
      );
    }
    return all.sort((a, b) => a.numero.localeCompare(b.numero));
  }, [rows, tipo, ordenMonto, hideZerosInMontoOrder]);

  const totales = useMemo(() => {
    if (tipo === 'NORMAL') {
      return {
        vendido: numerosOrdenados.reduce(
          (s, r) => s + (Number(r.vendido) || 0),
          0,
        ),
      };
    }
    if (tipo === 'REVENTADO') {
      return {
        rev1: numerosOrdenados.reduce(
          (s, r) => s + (Number(r.vendido_rev1) || 0),
          0,
        ),
        rev2: numerosOrdenados.reduce(
          (s, r) => s + (Number(r.vendido_rev2) || 0),
          0,
        ),
      };
    }
    // MEGA: mismos campos que REVENTADO
    return {
      rev1: numerosOrdenados.reduce(
        (s, r) => s + (Number(r.vendido_rev1) || 0),
        0,
      ),
      rev2: numerosOrdenados.reduce(
        (s, r) => s + (Number(r.vendido_rev2) || 0),
        0,
      ),
    };
  }, [numerosOrdenados, tipo]);

  // Total global del sorteo (suma de todos los números) — base para participación
  const grandTotal = useMemo(
    () => numerosOrdenados.reduce((s, r) => s + getTotalForRow(r, tipo), 0),
    [numerosOrdenados, tipo],
  );

  const cols = chunkCols(numerosOrdenados, COLS);
  const perCol = Math.max(...cols.map(c => c.length), 0);

  const handlePrint = async () => {
    const el = printRef.current;
    if (!el) return;

    const isSunmi = isNativePrintSelected();
    console.log('[PRINT GRID] ─────────────────────────────────');
    console.log('[PRINT GRID] componente: NumerosGrid');
    console.log('[PRINT GRID] isNativePrintSelected:', isSunmi);

    // APK nativa: texto térmico
    if (isSunmi) {
      console.log('[PRINT GRID] ruta nativa seleccionada');
      try {
        const lines: string[] = [];
        const hr = '================================';
        lines.push('');
        lines.push('LISTA DE NUMEROS');
        lines.push(hr);

        // Generar filas de números
        for (const r of numerosOrdenados) {
          const num = String(r.numero).padStart(2, '0');
          if (tipo === 'NORMAL') {
            const vendido = fmt(r.vendido || 0);
            lines.push(`${num}  C${vendido}`);
          } else {
            const rev1 = fmt(r.rev1 || r.vendido_rev1 || 0);
            const rev2 = fmt(r.rev2 || r.vendido_rev2 || 0);
            lines.push(`${num}  R1:${rev1}  R2:${rev2}`);
          }
        }

        // Total
        lines.push(hr);
        if (tipo === 'NORMAL') {
          const t = totales as { vendido: number };
          lines.push(`TOTAL: C${fmt(t.vendido)}`);
        } else {
          const t = totales as { rev1: number; rev2: number };
          lines.push(`TOTAL R1: C${fmt(t.rev1)}`);
          lines.push(`TOTAL R2: C${fmt(t.rev2)}`);
          lines.push(`TOTAL GENERAL: C${fmt(t.rev1 + t.rev2)}`);
        }
        lines.push(hr);
        lines.push('\n\n');

        const text = lines.join('\n');
        console.log('[PRINT GRID] [WORKING PATH] texto:', text.length, 'chars');
        const result = await printRawByDestination(text, 'NUMEROS GRID');
        if (!result.ok) throw new Error(result.error || 'Impresion falló');
        console.log('[PRINT GRID] ✓ impresion exitosa');
        console.log('[PRINT GRID] ─────────────────────────────────');
        return;
      } catch (printErr: any) {
        console.error('[PRINT GRID] ✗ impresion fallo:', printErr?.message);
        // Fall through to web
      }
    }

    // Web: HTML popup
    console.log('[PRINT GRID] ruta web seleccionada');
    const w = window.open('', '_blank', 'width=1100,height=800');
    if (!w) {
      console.log('[PRINT GRID] ─────────────────────────────────');
      return;
    }

    let totalLine = '';
    if (tipo === 'NORMAL') {
      const t = totales as { vendido: number };
      totalLine = `TOTAL MONTO: ₡${fmt(t.vendido)}`;
    } else if (tipo === 'REVENTADO' || tipo === 'MEGA') {
      const t = totales as { rev1: number; rev2: number };
      totalLine = `TOTAL R1: ₡${fmt(t.rev1)} &nbsp;|&nbsp; TOTAL R2: ₡${fmt(t.rev2)} &nbsp;|&nbsp; TOTAL GENERAL: ₡${fmt(t.rev1 + t.rev2)}`;
    }

    w.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>Lista de ventas</title>
  <meta charset="utf-8" />
  <style>
    body { font-family: monospace; font-size: 12px; margin: 8px; }
    table { border-collapse: collapse; width: 100%; }
    td, th { border: 1px solid #ccc; padding: 2px 5px; white-space: nowrap; }
    th { background: #e5e7eb; font-weight: bold; }
    .sep-col { border-right: 1px solid #e5e7eb !important; }
    .sep-row td { border-bottom: 1px solid #e5e7eb !important; }
    .total-bar { margin-top: 6px; padding: 5px 8px; background: #e5e7eb; font-weight: bold; border: 1px solid #ccc; }
  </style>
</head>
<body>
  ${el.innerHTML}
  <div class="total-bar">${totalLine}</div>
</body>
</html>`);
    w.document.close();
    w.focus();
    w.print();
    console.log('[PRINT GRID] ✓ web popup lanzado');
    console.log('[PRINT GRID] ─────────────────────────────────');
  };

  // Genera las celdas de una fila para cada columna
  const renderRow = (rowIdx: number) => {
    const isDecadeBorder = !ordenMonto && (rowIdx + 1) % 10 === 0;
    const rowStyle: React.CSSProperties = {
      borderBottom: isDecadeBorder ? '1px solid #e5e7eb' : '1px solid #f1f5f9',
    };

    const cells = cols.map((colRows, ci) => {
      const r = colRows[rowIdx];
      const last = ci === COLS - 1;
      const rightBorder = last ? '1px solid #d1d5db' : '1px solid #e5e7eb';

      if (!r) {
        const colSpan = tipo === 'NORMAL' ? 2 : 3;
        return (
          <td
            key={ci}
            colSpan={colSpan}
            style={{ padding: '3px 6px', borderRight: rightBorder }}
          />
        );
      }

      const total = getTotalForRow(r, tipo);
      const participacion = grandTotal > 0 ? total / grandTotal : 0;
      const clr = getParticipacionColor(participacion);
      const innerBdr = '1px solid rgba(0,0,0,0.1)';
      // Flags de restricción/top tienen prioridad sobre heatmap de participación
      const cellStyle: React.CSSProperties = {
        transition: 'background-color 0.15s ease',
        ...(r._restringido ? { backgroundColor: '#dc2626', color: '#fff' } :
            r._top ? { backgroundColor: '#fbbf24', color: '#1c1917' } :
            r._highlight ? { backgroundColor: '#fef08a', color: '#333' } :
            clr),
      };

      if (tipo === 'NORMAL') {
        return (
          <React.Fragment key={ci}>
            <td style={{ ...cellStyle, fontWeight: 'bold', padding: '3px 6px', borderRight: innerBdr }}>
              {r.numero}
            </td>
            <td style={{ ...cellStyle, padding: '3px 6px', borderRight: rightBorder }}>
              {fmt(Number(r.vendido) || 0)}
            </td>
          </React.Fragment>
        );
      }

      if (tipo === 'REVENTADO') {
        return (
          <React.Fragment key={ci}>
            <td style={{ ...cellStyle, fontWeight: 'bold', padding: '3px 6px', borderRight: innerBdr }}>
              {r.numero}
            </td>
            <td style={{ ...cellStyle, padding: '3px 6px', borderRight: innerBdr }}>
              {fmt(Number(r.vendido_rev1) || 0)}
            </td>
            <td style={{ ...cellStyle, padding: '3px 6px', borderRight: rightBorder }}>
              {fmt(Number(r.vendido_rev2) || 0)}
            </td>
          </React.Fragment>
        );
      }

      // MEGA: igual que REVENTADO (#, R1, R2)
      if (tipo === 'MEGA') {
        return (
          <React.Fragment key={ci}>
            <td style={{ ...cellStyle, fontWeight: 'bold', padding: '3px 6px', borderRight: innerBdr }}>
              {r.numero}
            </td>
            <td style={{ ...cellStyle, padding: '3px 6px', borderRight: innerBdr }}>
              {fmt(Number(r.vendido_rev1) || 0)}
            </td>
            <td style={{ ...cellStyle, padding: '3px 6px', borderRight: rightBorder }}>
              {fmt(Number(r.vendido_rev2) || 0)}
            </td>
          </React.Fragment>
        );
      }
    });

    return (
      <tr key={rowIdx} style={rowStyle}>
        {cells}
      </tr>
    );
  };

  // Cabecera de columna
  const renderHeaderGroup = (ci: number) => {
    const last = ci === COLS - 1;
    const thSep: React.CSSProperties = {
      ...thBase,
      borderRight: last ? '1px solid #d1d5db' : '3px solid #555',
    };
    if (tipo === 'NORMAL') {
      return (
        <React.Fragment key={ci}>
          <th style={thBase}>#</th>
          <th style={thSep}>MONTO</th>
        </React.Fragment>
      );
    }
    if (tipo === 'REVENTADO') {
      return (
        <React.Fragment key={ci}>
          <th style={thBase}>#</th>
          <th style={thBase}>R1</th>
          <th style={thSep}>R2</th>
        </React.Fragment>
      );
    }
    // MEGA: igual que REVENTADO (#, R1, R2)
    if (tipo === 'MEGA') {
      return (
        <React.Fragment key={ci}>
          <th style={thBase}>#</th>
          <th style={thBase}>R1</th>
          <th style={thSep}>R2</th>
        </React.Fragment>
      );
    }
  };

  return (
    <div>
      {/* Controles */}
      <div
        style={{
          display: 'flex',
          gap: '6px',
          marginBottom: '8px',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <button
          onClick={() => setOrdenMonto(false)}
          style={{
            ...btnBase,
            background: !ordenMonto ? '#1d4ed8' : '#fff',
            color: !ordenMonto ? '#fff' : '#374151',
            fontWeight: !ordenMonto ? 'bold' : 'normal',
          }}
        >
          Por número
        </button>
        <button
          onClick={() => setOrdenMonto(true)}
          style={{
            ...btnBase,
            background: ordenMonto ? '#1d4ed8' : '#fff',
            color: ordenMonto ? '#fff' : '#374151',
            fontWeight: ordenMonto ? 'bold' : 'normal',
          }}
        >
          Mayor monto ↓
        </button>
        {!hidePrintButton && (
          <button
            onClick={handlePrint}
            style={{ ...btnBase, background: '#fff', color: '#374151', marginLeft: 'auto' }}
            title="Abrir ventana de impresión"
          >
            🖨️ Imprimir
          </button>
        )}
      </div>

      {/* Leyenda de colores */}
      <div
        style={{
          display: 'flex',
          gap: '5px',
          marginBottom: '8px',
          fontSize: '12px',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ backgroundColor: '#f3f4f6', color: '#9ca3af', padding: '2px 7px', border: '1px solid #d1d5db' }}>
          Sin ventas / ≤3%
        </span>
        <span style={{ backgroundColor: '#86efac', color: '#14532d', padding: '2px 7px', border: '1px solid #d1d5db' }}>
          &gt;3%
        </span>
        <span style={{ backgroundColor: '#fde047', color: '#713f12', padding: '2px 7px', border: '1px solid #d1d5db' }}>
          &gt;7%
        </span>
        <span style={{ backgroundColor: '#fb923c', color: '#7c2d12', padding: '2px 7px', border: '1px solid #d1d5db' }}>
          &gt;15%
        </span>
        <span style={{ backgroundColor: '#ef4444', color: '#fff', padding: '2px 7px', border: '1px solid #d1d5db' }}>
          &gt;25%
        </span>
      </div>

      {/* Tabla (referenciada para imprimir) */}
      <div
        ref={printRef}
        style={{
          border: '1px solid #d1d5db',
          backgroundColor: '#fff',
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontFamily: 'monospace',
            fontSize: '13px',
          }}
        >
          <thead>
            <tr>
              {Array.from({ length: COLS }).map((_, ci) => renderHeaderGroup(ci))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: perCol }).map((_, rowIdx) => renderRow(rowIdx))}
          </tbody>
        </table>

      </div>

      {/* Totales */}
      <div
        style={{
          marginTop: '4px',
          padding: '7px 10px',
          background: '#e5e7eb',
          fontFamily: 'monospace',
          fontSize: '13px',
          fontWeight: 'bold',
          border: '1px solid #d1d5db',
          display: 'flex',
          gap: '16px',
          flexWrap: 'wrap',
        }}
      >
        {tipo === 'NORMAL' && (
          <span>TOTAL MONTO: ₡{fmt((totales as { vendido: number }).vendido)}</span>
        )}
        {(tipo === 'REVENTADO' || tipo === 'MEGA') && (() => {
          const t = totales as { rev1: number; rev2: number };
          return (
            <>
              <span>TOTAL R1: ₡{fmt(t.rev1)}</span>
              <span>TOTAL R2: ₡{fmt(t.rev2)}</span>
              <span>TOTAL GENERAL: ₡{fmt(t.rev1 + t.rev2)}</span>
            </>
          );
        })()}
      </div>
    </div>
  );
});
